import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LuArrowRight, LuCheck, LuX } from 'react-icons/lu';
import HeaderOne from '../../header/HeaderOne';
import FooterOne from '../../footer/FooterOne';
import Head from '../../components/Head';
import '../industry.css';

const PAIN = [
  'Twilio Verify at $0.05 per OTP — a single 100k MAU month is $5,000 in OTPs alone',
  'A2P 10DLC registration before you can launch your sign-up flow at scale',
  'Per-region carrier surcharges that quietly inflate your COGS month over month',
  'Tech debt in your auth code: token gen, expiry, retry, brute-force protection — all you',
];

const GAIN = [
  '$0.001 per OTP. Same 100k MAU month: $100. Yes, two zeros',
  'P2P SMS through your own SIM — no registration, ship the OTP flow today',
  'Single flat rate worldwide. 180+ countries, no surcharge tables to memorize',
  'Verification API generates + matches the code. Two endpoints, no storage',
];

const DAY = [
  { time: 'T+0ms', title: 'User submits phone on signup', body: 'Your backend POSTs /api/send-verification with the number. We return a verification_id.' },
  { time: 'T+800ms', title: 'OTP delivered', body: 'Code arrives on the user\'s phone. Real consumer SIM means inbox, not spam folder.' },
  { time: 'T+12s', title: 'User types code', body: 'Your frontend POSTs verification_id + code to /api/check-verification. We return { verified: true } or false.' },
  { time: 'T+12.1s', title: 'Account activated', body: 'You issue your session token. No code storage on your side, no expiry logic, no rate-limit handling.' },
  { time: 'Later that day', title: 'Login attempt fires 2FA', body: 'Same two endpoints, different context. Same $0.001 per code, same dashboard, same audit log.' },
];

const FAQ = [
  {
    q: 'How does this compare to Twilio Verify or Auth0\'s SMS feature?',
    a: 'Twilio Verify is $0.05/attempt + carrier fees. Auth0 charges per MAU. TextLink is a flat $0.001 per SMS through your SIM — same compliance posture, dramatically lower cost. Same two-step API as Verify (send + check) so migration is straightforward.',
  },
  {
    q: 'What about international users? Surcharge nightmares?',
    a: 'No surcharge tables. One flat rate worldwide because the SMS leaves your SIM at your carrier\'s P2P rate. Your only variable is the SIM\'s plan — most users run a US SIM for US/CA traffic plus a UK SIM for EU.',
  },
  {
    q: 'Can I run transactional alerts (not just OTP)?',
    a: 'Absolutely. The plain SMS API powers password resets, security alerts, payment receipts, account changes — anything transactional. Webhooks fire status callbacks back to your endpoint for delivery tracking.',
  },
  {
    q: 'How do I scale past one SIM\'s daily limit?',
    a: 'Add more SIMs in the dashboard. Outbound traffic load-balances across them automatically. Most production SaaS accounts run 3–10 SIMs depending on signup volume. Each SIM handles ~200–400 messages/day comfortably.',
  },
  {
    q: 'What about uptime — what if the phone hosting the SIM goes offline?',
    a: 'We monitor phone connectivity and notify you via webhook + email the moment a phone goes offline. Recommended setup: two paired phones per critical SIM as automatic failover during charging cycles or restarts.',
  },
];

export default function SAAS() {
  const navigate = useNavigate();

  return (
    <>
      <Head
        title="SMS API for SaaS — OTPs, Alerts & Notifications"
        description="Power OTPs, 2FA, and transactional SMS for your SaaS at $0.001 per message. TextLink generates and matches verification codes — two REST endpoints, 180+ countries, no per-region surcharges."
        canonical="/saas"
      />
      <HeaderOne />

      <main className="iv">
        <div className="iv-shell">

          {/* HERO */}
          <header className="iv-hero">
            <div className="iv-hero-left">
              <span className="iv-eyebrow">SaaS</span>
              <h1 className="iv-h1">
                OTP, 2FA & alerts<br />
                <span className="iv-h1-accent">at one cent each.</span>
              </h1>
              <p className="iv-lead">
                Wire SMS verification into your SaaS in an afternoon. Two REST endpoints, $0.001 per code, 180+ countries — no Twilio Verify markup, no per-region surcharges, no 10DLC delays.
              </p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--primary" onClick={() => navigate('/signup')}>
                  Get an API key <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <a className="iv-btn iv-btn--ghost" href="https://docs.textlinksms.com/" target="_blank" rel="noopener noreferrer">
                  Read the docs
                </a>
              </div>
            </div>

            <div className="iv-hero-stats">
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">$0.001</div>
                <div className="iv-stat-label">per OTP — vs $0.05 on Twilio Verify</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">2</div>
                <div className="iv-stat-label">REST endpoints. Send code, check code.</div>
              </div>
              <div className="iv-stat-tile">
                <div className="iv-stat-num">180+</div>
                <div className="iv-stat-label">countries — flat rate, no surcharges</div>
              </div>
              <div className="iv-stat-tile iv-stat-tile--accent">
                <div className="iv-stat-num">0</div>
                <div className="iv-stat-label">code storage on your side. We match.</div>
              </div>
            </div>
          </header>

          {/* PAIN / GAIN */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">SMS auth is a tax. Stop paying it.</h2>
              <p className="iv-sub">Every growing SaaS hits the moment when verification SMS becomes a top-5 line item. There's a better way.</p>
            </div>

            <div className="iv-contrast">
              <div className="iv-contrast-panel iv-contrast-panel--bad">
                <span className="iv-contrast-eyebrow">Twilio Verify / Auth0 SMS</span>
                <h3 className="iv-contrast-h3">What you're paying for today</h3>
                <ul className="iv-contrast-list">
                  {PAIN.map((p, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuX size={12} strokeWidth={2.5} /></span>
                      <span>{p}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="iv-contrast-panel iv-contrast-panel--good">
                <span className="iv-contrast-eyebrow">TextLink</span>
                <h3 className="iv-contrast-h3">What changes with your own SIM</h3>
                <ul className="iv-contrast-list">
                  {GAIN.map((g, i) => (
                    <li key={i}>
                      <span className="iv-contrast-icon"><LuCheck size={12} strokeWidth={2.5} /></span>
                      <span>{g}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* TIMELINE */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">An OTP, end to end</h2>
              <p className="iv-sub">From form submit to verified user in under 15 seconds — the whole flow, with timings.</p>
            </div>

            <ul className="iv-day">
              {DAY.map((d, i) => (
                <li key={i} className="iv-day-row">
                  <div className="iv-day-time">{d.time}</div>
                  <div className="iv-day-body">
                    <h3 className="iv-day-title">{d.title}</h3>
                    <p className="iv-day-text">{d.body}</p>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          {/* NUMBERS */}
          <section className="iv-section">
            <div className="iv-numbers">
              <div className="iv-num">
                <div className="iv-num-big"><span>50×</span></div>
                <div className="iv-num-label">cheaper per OTP than Twilio Verify</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">15s</div>
                <div className="iv-num-label">end-to-end OTP flow latency</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">2 EP</div>
                <div className="iv-num-label">REST endpoints. That's the whole SDK.</div>
              </div>
              <div className="iv-num">
                <div className="iv-num-big">180+</div>
                <div className="iv-num-label">countries. Flat rate. No surcharges.</div>
              </div>
            </div>
          </section>

          {/* USE CASES — alternating */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">SMS workflows in modern SaaS</h2>
              <p className="iv-sub">Three patterns that ship in every B2B and consumer app.</p>
            </div>

            <div className="iv-cases">

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Phone verification (signup)</span>
                  <h3 className="iv-case-h3">Verify users without writing OTP boilerplate.</h3>
                  <p className="iv-case-p">
                    Two endpoints — <code>send-verification</code> and <code>check-verification</code>. We generate, deliver, expire, and match the code. Your auth flow goes from 200 lines of code-storage logic to two API calls.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Configurable expiry (60s–1hr per request)</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Built-in rate limiting per phone number</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Custom message template — brand it your way</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — Signup OTP</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Your Acme code is <strong>847291</strong>. Expires in 5 minutes.
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">User typed it back: ✓</div>
                  </div>
                </div>
              </div>

              <div className="iv-case iv-case--reverse">
                <div className="iv-case-text">
                  <span className="iv-case-tag">2FA on login</span>
                  <h3 className="iv-case-h3">Step-up auth for sensitive actions.</h3>
                  <p className="iv-case-p">
                    Same Verification API, different context. Trigger 2FA on login, password change, payment method update, or any high-risk action. Audit logs are stored automatically.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Drop-in replacement for Twilio Verify</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Same $0.001 cost across all use cases</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Webhooks for verification events into your SIEM</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — Login 2FA</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Acme login from new device (Chrome, San Francisco). Code: <strong>402817</strong>. Not you? Reply STOP.
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">$0.001 — vs $0.05 on Twilio</div>
                  </div>
                </div>
              </div>

              <div className="iv-case">
                <div className="iv-case-text">
                  <span className="iv-case-tag">Transactional alerts</span>
                  <h3 className="iv-case-h3">Notifications, receipts, and security pings.</h3>
                  <p className="iv-case-p">
                    The plain SMS API handles everything that isn't OTP — password resets, payment confirmations, security alerts, billing reminders. Same SIM, same dashboard, same flat rate.
                  </p>
                  <ul className="iv-case-bullets">
                    <li><LuCheck size={15} strokeWidth={2.5} /> Status webhooks for delivery tracking</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> Two-way replies route to your support inbox</li>
                    <li><LuCheck size={15} strokeWidth={2.5} /> JS, Python, PHP, C# SDKs</li>
                  </ul>
                </div>
                <div className="iv-case-visual">
                  <div className="iv-msg-card">
                    <div className="iv-msg-card-title">Outbound — Payment receipt</div>
                    <div className="iv-msg-bubble iv-msg-bubble--out">
                      Acme: $49 charged to card ending 4242. Plan renewed through Apr 12, 2026. Manage billing: tlsms.co/b
                    </div>
                    <div className="iv-msg-meta iv-msg-meta--right">Webhook → Stripe → SMS → User</div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* FAQ */}
          <section className="iv-section">
            <div className="iv-section-head">
              <h2 className="iv-h2">SaaS questions, answered</h2>
              <p className="iv-sub">Migration, scaling, compliance — what every CTO asks before flipping providers.</p>
            </div>

            <div className="iv-faq">
              {FAQ.map((f, i) => (
                <div key={i} className="iv-faq-item">
                  <h3 className="iv-faq-q">{f.q}</h3>
                  <p className="iv-faq-a">{f.a}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FINAL CTA */}
          <section className="iv-section">
            <div className="iv-final">
              <h2>Cut your OTP bill by 50×.</h2>
              <p>300 free messages on signup. Two endpoints. An afternoon to migrate.</p>
              <div className="iv-cta-row">
                <button className="iv-btn iv-btn--white" onClick={() => navigate('/signup')}>
                  Get an API key <LuArrowRight size={15} strokeWidth={2} />
                </button>
                <a className="iv-btn iv-btn--white-ghost" href="https://docs.textlinksms.com/" target="_blank" rel="noopener noreferrer">
                  Read the docs
                </a>
              </div>
            </div>
          </section>

        </div>
      </main>

      <FooterOne />
    </>
  );
}
